import { useEffect } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { useOrganizationSoundPolicyQuery } from '@/graphql/generated/hooks'
import {
  markCriticalSoundPolicySyncStarted,
  resetCriticalSoundPolicySyncStart,
} from '@/hooks/criticalSoundPolicyGrace'
import { useAppDispatch } from '@/redux/hooks'
import { setAlwaysPlayCriticalIncidentSound } from '@/redux/ui/uiSlice'

/**
 * How often an open session re-reads the policy. A PM can change it at any
 * time and GSOC screens stay open for days, so without this an operator would
 * only pick the change up on their next login. The query is tiny (one boolean).
 */
export const CRITICAL_SOUND_POLICY_POLL_INTERVAL_MS = 60_000

interface UseCriticalIncidentSoundPolicySyncIProps {
  /** A valid, logged-in user. Nothing is fetched before that. */
  isEnabled: boolean
}

/**
 * Keeps the org-wide "Always play sound for Critical incidents" policy
 * (ENG-35923) in Redux for every role, the way `KeyCloakProvider` keeps the
 * per-user audio setting there, so incident playback can read it synchronously.
 *
 * The policy only does anything where the `incidentAudioAlerts` flag is on (it
 * gates all incident audio); with it off nothing is fetched. If a refresh fails the last value is kept rather than
 * reset, so a network blip can't silence Critical incidents; logout resets it
 * (see `resetUi`) so it never carries over to another organization.
 */
export const useCriticalIncidentSoundPolicySync = ({
  isEnabled,
}: UseCriticalIncidentSoundPolicySyncIProps): void => {
  const dispatch = useAppDispatch()
  const isIncidentAudioEnabled = useFeatureFlag(FeatureFlag.incidentAudioAlerts)
  const isActive = isEnabled && isIncidentAudioEnabled

  const { data } = useOrganizationSoundPolicyQuery({
    // Network first, so a value cached by an earlier session never flashes in.
    // It still writes to the cache, so a PM's change from the Account settings
    // page reaches this session's Redux value without a refetch. It also keeps a
    // poll that is in flight during a save from delivering the older value into
    // Redux: Apollo holds back cache updates for a network-only query until its
    // request finishes (see the stale-poll test).
    fetchPolicy: 'network-only',
    // `skip` doesn't stop a query that is already polling, so zero the interval too.
    pollInterval: isActive ? CRITICAL_SOUND_POLICY_POLL_INTERVAL_MS : 0,
    skip: !isActive,
  })
  const isPolicyOn =
    data?.currentOrganization?.soundPolicy?.alwaysPlayCriticalIncidentSound

  // The grace period for an unknown policy starts when the sync does (see
  // criticalSoundPolicyGrace); logging out or turning incident audio off ends it.
  useEffect(() => {
    if (isActive) markCriticalSoundPolicySyncStarted()
    else resetCriticalSoundPolicySyncStart()
  }, [isActive])

  useEffect(() => {
    // Undefined while loading or after a failed fetch: keep what we have.
    if (isActive && isPolicyOn !== undefined) {
      dispatch(setAlwaysPlayCriticalIncidentSound(isPolicyOn))
    }
  }, [dispatch, isActive, isPolicyOn])
}
